// Core modules
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as _ from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AppFeatures, ConfigService } from '@services/config.service';
import { RolesService, ViewService, AuthenticationService } from '@services/services.module';

import { SurvaleCommonComponent } from '../../../../common/survale-common.component';
import { WidgetsCommon } from '@components/survale-widgets/widgets-common/widgets-common';

// import humanizeString = require('humanize-string');

/**
 * Client settings component
 */
@Component({
  selector: 'app-client-settings',
  templateUrl: './client-settings.component.html',
  styleUrls: ['./client-settings.component.scss'],
  standalone: false
})
export class ClientSettingsComponent extends SurvaleCommonComponent implements OnInit, OnChanges, OnDestroy {
  @Input() settings: any;

  public clientSettingsForm: UntypedFormGroup;
  public slackBotForm: UntypedFormGroup;
  public filteredTimeZones = [];
  public tzFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public roles;
  public views;
  public features = _.values(AppFeatures);
  public confidenceLevelOptions = WidgetsCommon.CONFIDENCE_LEVEL_OPTIONS;
  public tokenExpiryOptions = [
    { k: 30, v: '30 minutes' },
    { k: 60, v: '1 Hour' },
    { k: 3 * 60, v: '3 Hours' },
    { k: 6 * 60, v: '6 Hours' },
    { k: 8 * 60, v: '8 Hours' },
    { k: 24 * 60, v: '1 Day' },
    { k: 3 * 24 * 60, v: '3 Days' },
    { k: 5 * 24 * 60, v: '5 Days' },
    { k: 7 * 24 * 60, v: '1 Week' },
  ];
  protected _onDestroy = new Subject<void>();

  /**
   * Life cycle method
   */
  constructor(
    private fb: UntypedFormBuilder,
    private rolesService: RolesService,
    private viewService: ViewService,
    public authenticationService: AuthenticationService,

  ) {
    super(authenticationService);
    this.filteredTimeZones = ConfigService.getTimeZones();
    // console.log('this.filteredTimeZones', this.filteredTimeZones);
  }

  get apiHost(): string {
    return ConfigService.config.apiHost;
  }
  /**
   * Life cycle init method
   */
  ngOnInit(): void {

    this.clientSettingsForm = this.fb.group({
      atsAuthProvider: new UntypedFormControl(),
      indexCalculations: new UntypedFormControl(),
      compareWidgetsEnabled: new UntypedFormControl(),
      showConfidenceInWidgets: new UntypedFormControl(),
      confidenceLevel: new UntypedFormControl('95'),
      denominatorMultiplier: new UntypedFormControl(1),
      insightsEnabled: new UntypedFormControl(),
      operatingCountry: new UntypedFormControl(),
      smsEnabled: new UntypedFormControl(),
      forcePwdChange: new UntypedFormControl(),
      enableLiveSession: new UntypedFormControl(),
      clientName: new UntypedFormControl(),
      clientTimezone: new UntypedFormControl(),
      defaultManagerUserRole: new UntypedFormControl(),
      defaultInsightUserRole: new UntypedFormControl(),
      defaultManagerUserViewIds: new UntypedFormControl(),
      defaultInsightUserViewIds: new UntypedFormControl(),
      hasBenchMarks: new UntypedFormControl('yes'),
      featureWhiteList: new UntypedFormControl(),
      tokenExpiryInMinutes: new UntypedFormControl(),
      // needsSurveyResponseEscalation: new FormControl(),
      smsNumber: new UntypedFormControl(),
      calendarQuartersDisabled: new UntypedFormControl(),
      users: new UntypedFormControl(),
      views: new UntypedFormControl(),
      portalSurveyLinkExpire: new UntypedFormControl(false),
      portalSurveyLinkExpirationDays: new UntypedFormControl(45),
      teamsWelcomeMessage: new UntypedFormControl(),
      slackWelcomeMessage: new UntypedFormControl(),
    });
    this.slackBotForm = this.fb.group({
      botUserOAuthToken: new UntypedFormControl(),
    });

    this.rolesService.getRoles().subscribe(data => this.roles = data.body);
    this.viewService.getViewsForUser().subscribe(views => this.views = views.body);
    this.tzFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((): void => {
        this.filterTimeZones();
      });

  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  filterTimeZones(): void {
    // @ts-ignore
    const timeZones = ConfigService.getTimeZones();
    const filterOn = this.tzFilterCtrl.value;
    this.filteredTimeZones =
      _.filter(timeZones, s => s.value.toLowerCase().indexOf(filterOn.toLowerCase()) > -1);
    // console.log('this.filteredTimeZones', this.filteredTimeZones);
  }

  defaultValues() {
    if (!this.settings) {
      return;
    }
    const defaultSurveyEscalation = {
      reportToEmail: [],
      customLookoutKeyWords: [
        'worried',
        'disappointed',
        'delay',
        'delayed',
        'irresponsible',
        'non responsive',
        'unresponsive'],
      longCommentsMinChars: 50,
      schedule: 'weekly',
      hours: 1,
      minScore: 3,
      immediateAlert: {
        minScore: { enabled: true, value: 3 },
        alertLongComments: { enabled: true, value: 3 }
      },
      npsAlert: []
    };
    this.settings.surveyEscalation = _.defaults(this.settings.surveyEscalation, defaultSurveyEscalation);
    // this.settings.needsSurveyResponseEscalation = this.settings.needsSurveyResponseEscalation || 0;
    const defaultsFalse = [
      'insightsEnabled', 'compareWidgetsEnabled', 'showConfidenceInWidgets', 'smsEnabled', 'forcePwdChange',
      'enableLiveSession', 'calendarQuartersDisabled'
    ];
    _.each(defaultsFalse, v => {
      this.settings[v] = this.settings[v] || false;
    });
    // Not booleans, so they don't belong in defaultsFalse above — client-level confidence
    // settings always have a concrete value (there's no "inherit" concept at this level).
    this.settings.confidenceLevel = this.settings.confidenceLevel || '95';
    this.settings.denominatorMultiplier = this.settings.denominatorMultiplier || 1;
  }

  /**
   * Set the form value when received from parent
   */
  ngOnChanges(): void {
    this.defaultValues();
    if (this.clientSettingsForm) {
      this.settings = this.settings || {};
      _.defaults(this.settings, { hasBenchMarks: 'yes' });
      if (_.isNil(this.settings.hasBenchMarks)) {
        this.settings.hasBenchMarks = 'yes';
      }
      console.log('this.settings', this.settings);
      this.clientSettingsForm.patchValue(this.settings);
    }
    if (this.slackBotForm) {
      this.slackBotForm.patchValue(this.settings.slackBotSettings);
    }
  }
}
